import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Globe, CheckCircle, Heart, Users, ArrowRight, Handshake, Building, FileText, Network } from 'lucide-react';
import { Button } from '../components/ui/button';

const PAYPAL_LINK = "https://www.paypal.com/donate/?hosted_button_id=ZUX3BKRN524VS";

export default function InternationalCooperationPage() {
  return (
    <div data-testid="international-cooperation-page" className="pt-20">
      {/* Hero Section */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#7BC4E2] via-[#8BCDE8] to-[#9BD6EE]">
          <div className="absolute top-0 right-0 w-[400px] h-[350px] bg-[#4A7C59]/30 rounded-bl-[200px]" />
          <div className="absolute bottom-0 left-0 w-[300px] h-[250px] bg-white/20 rounded-tr-[150px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <motion.div
            className="text-center max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Globe className="w-10 h-10 text-slate-900" />
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-['Manrope'] text-slate-900 mb-6">
              International Cooperation & Development
            </h1>
            <p className="text-xl text-slate-800">
              Fostering global partnerships for sustainable development worldwide
            </p>
          </motion.div>
        </div>
      </section>

      {/* Featured Images */}
      <section className="relative py-12 overflow-hidden bg-[#FDF8F4]">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img 
                src="https://images.unsplash.com/photo-1517048676732-d65bc937f952?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="OPAD International Cooperation - Global Partnerships"
                className="w-full h-72 object-cover rounded-3xl shadow-2xl"
              />
              <p className="text-center text-slate-600 mt-4 italic">International partnership meetings and collaboration</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <img 
                src="https://images.unsplash.com/photo-1552664730-d307ca884978?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="OPAD International Cooperation - Development Planning"
                className="w-full h-72 object-cover rounded-3xl shadow-2xl"
              />
              <p className="text-center text-slate-600 mt-4 italic">Strategic development planning sessions</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-white">
          <div className="absolute top-0 left-0 w-[500px] h-[450px] bg-[#7BC4E2]/10 rounded-br-[250px]" />
          <div className="absolute bottom-0 right-0 w-[400px] h-[350px] bg-[#4A7C59]/10 rounded-tl-[180px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <motion.div
            className="max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="bg-white/90 backdrop-blur-sm p-8 md:p-12 rounded-3xl shadow-lg border border-slate-100">
              <p className="text-slate-700 leading-relaxed text-lg mb-6">
                At the <strong className="text-[#4A7C59]">Organization for Poverty Alleviation and Development (OPAD)</strong>, our International Cooperation and Development programmes are designed to foster strategic partnerships that support sustainable development, humanitarian action, and inclusive growth across diverse communities worldwide.
              </p>
              
              <p className="text-slate-700 leading-relaxed text-lg mb-6">
                Recognizing that global challenges such as <strong className="text-[#E08B7A]">poverty, inequality, climate change, and social exclusion</strong> require collaborative solutions, OPAD works closely with international institutions, governments, civil society organizations, and local stakeholders to implement effective development initiatives.
              </p>
              
              <p className="text-slate-700 leading-relaxed text-lg mb-6">
                Our programmes focus on facilitating <strong className="text-[#7BC4E2]">knowledge exchange, technical cooperation, and capacity-building initiatives</strong> that strengthen institutional frameworks and community resilience. Through collaborative development projects, OPAD promotes innovation, social inclusion, and economic empowerment while supporting policy dialogue and evidence-based decision-making processes that enhance development outcomes.
              </p>
              
              <p className="text-slate-700 leading-relaxed text-lg">
                We also support transnational partnerships that address shared challenges through sustainable solutions and resource mobilization. By fostering cooperation between local and global actors, OPAD enhances the effectiveness of development interventions and promotes long-term impact. Our work contributes to advancing international development priorities and strengthening global solidarity in addressing complex humanitarian and socio-economic challenges.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What We Do */}
      <section className="relative section-padding overflow-hidden bg-[#FDF8F4]">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-[450px] h-[400px] bg-[#7BC4E2]/15 rounded-bl-[200px]" />
          <div className="absolute bottom-0 left-0 w-[350px] h-[300px] bg-[#E5B75F]/10 rounded-tr-[150px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-slate-900 mb-4">What We Do</h2>
            <div className="w-24 h-1 bg-[#7BC4E2] mx-auto rounded-full"></div>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <motion.div
              className="bg-white/90 backdrop-blur-sm p-8 rounded-2xl shadow-lg border-t-4 border-[#7BC4E2]"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="w-14 h-14 bg-[#7BC4E2]/10 rounded-xl flex items-center justify-center mb-4">
                <Handshake className="w-7 h-7 text-[#7BC4E2]" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Partnership Development & Coordination</h3>
              <p className="text-slate-600">Facilitating international partnerships for collaborative development initiatives.</p>
            </motion.div>
            
            <motion.div
              className="bg-white/90 backdrop-blur-sm p-8 rounded-2xl shadow-lg border-t-4 border-[#4A7C59]"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="w-14 h-14 bg-[#4A7C59]/10 rounded-xl flex items-center justify-center mb-4">
                <Building className="w-7 h-7 text-[#4A7C59]" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Capacity Building & Knowledge Exchange</h3>
              <p className="text-slate-600">Promoting technical cooperation and institutional strengthening.</p>
            </motion.div>
            
            <motion.div
              className="bg-white/90 backdrop-blur-sm p-8 rounded-2xl shadow-lg border-t-4 border-[#E5B75F]"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <div className="w-14 h-14 bg-[#E5B75F]/10 rounded-xl flex items-center justify-center mb-4">
                <FileText className="w-7 h-7 text-[#E5B75F]" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Policy Dialogue & Resource Mobilization</h3>
              <p className="text-slate-600">Supporting policy engagement and sustainable development financing.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Strategic Focus Areas */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-white">
          <div className="absolute top-0 left-0 w-[450px] h-[400px] bg-[#E5B75F]/10 rounded-br-[200px]" />
          <div className="absolute bottom-0 right-0 w-[350px] h-[300px] bg-[#7BC4E2]/10 rounded-tl-[150px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-slate-900 mb-6">
                Strategic Focus Areas
              </h2>
              
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#7BC4E2] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700 text-lg">Global Partnership Building</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#4A7C59] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700 text-lg">Technical Cooperation</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#E5B75F] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700 text-lg">Sustainable Development Planning</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#E08B7A] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700 text-lg">Resource Mobilization</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#7BC4E2] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700 text-lg">Cross-Border Collaboration</span>
                </li>
              </ul>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src="https://images.unsplash.com/photo-1531545514256-b1400bc00f31?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="International Cooperation"
                className="rounded-3xl shadow-xl w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Global Network */}
      <section className="relative section-padding overflow-hidden bg-[#FDF8F4]">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-[450px] h-[400px] bg-[#4A7C59]/10 rounded-bl-[200px]" />
          <div className="absolute bottom-0 left-0 w-[350px] h-[300px] bg-[#E08B7A]/10 rounded-tr-[150px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Network className="w-16 h-16 text-[#7BC4E2] mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-slate-900 mb-6">
              Our Global Network
            </h2>
            <p className="text-slate-700 text-lg mb-8">
              OPAD collaborates with international institutions, governments, UN bodies, EU agencies, 
              academic institutions, and civil society organizations worldwide to advance sustainable 
              development goals and create lasting positive impact.
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="bg-white/90 backdrop-blur-sm p-6 rounded-2xl shadow-lg">
                <p className="text-3xl font-bold text-[#7BC4E2]">20+</p>
                <p className="text-slate-600 text-sm">International Partners</p>
              </div>
              <div className="bg-white/90 backdrop-blur-sm p-6 rounded-2xl shadow-lg">
                <p className="text-3xl font-bold text-[#4A7C59]">15+</p>
                <p className="text-slate-600 text-sm">Countries</p>
              </div>
              <div className="bg-white/90 backdrop-blur-sm p-6 rounded-2xl shadow-lg">
                <p className="text-3xl font-bold text-[#E08B7A]">50+</p>
                <p className="text-slate-600 text-sm">Collaborative Projects</p>
              </div>
              <div className="bg-white/90 backdrop-blur-sm p-6 rounded-2xl shadow-lg">
                <p className="text-3xl font-bold text-[#E5B75F]">5</p>
                <p className="text-slate-600 text-sm">Continents</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#7BC4E2] via-[#8BCDE8] to-[#9BD6EE]">
          <div className="absolute top-0 right-0 w-[350px] h-[300px] bg-white/10 rounded-bl-[150px]" />
          <div className="absolute bottom-0 left-0 w-[300px] h-[250px] bg-[#4A7C59]/20 rounded-tr-[120px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <motion.div
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Globe className="w-16 h-16 text-slate-900 mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-slate-900 mb-6">
              Need Help? Partner With Us
            </h2>
            <p className="text-slate-800 text-lg mb-8">
              Join our global network and collaborate on sustainable development initiatives.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={PAYPAL_LINK} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#E08B7A] hover:bg-[#D07A69] text-white rounded-full px-8 py-6 text-lg font-semibold">
                  <Heart className="w-5 h-5 mr-2" />
                  Donate Now
                </Button>
              </a>
              <Link to="/contact">
                <Button className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-8 py-6 text-lg font-semibold">
                  <Handshake className="w-5 h-5 mr-2" />
                  Partner With Us
                </Button>
              </Link>
              <Link to="/programs">
                <Button variant="outline" className="border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white rounded-full px-8 py-6 text-lg font-semibold">
                  View All Programs
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
